import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { getRetailerToken } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { useMutation as useDeleteBoardMutation } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/boardToken/delete-hooks";
import { useQuery as useFetchBoardQuery, getQueryKey as getFetchBoardQueryKey } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/boardToken/get-hooks";
import { useMutation as useUpdateBoardMutation } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/boardToken/put-hooks";
import { getQueryKey as getFetchDefaultBoardQueryKey } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/default/get-hooks";
import { useMutation as useCreateBoardMutation } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/post-hooks";
import { QueryParameters } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/scroll/get";
import { useQuery as useFetchBoardsQuery, getQueryKey as getFetchBoardsQueryKey } from "@faire/web-api--source/api/v2/retailers/retailerToken/boards/scroll/get-hooks";
import { ICreateRetailerBoardRequest } from "@faire/web-api--source/indigofair/data/ICreateRetailerBoardRequest";
import { IRetailerBoard } from "@faire/web-api--source/indigofair/data/IRetailerBoard";
import { IUpdateRetailerBoardRequest } from "@faire/web-api--source/indigofair/data/IUpdateRetailerBoardRequest";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
var PARAMS = QueryParameters.build({
    all_items: true
});
export var useFetchAllBoards = function() {
    var retailerToken = getRetailerToken();
    var query = useFetchBoardsQuery(retailerToken, PARAMS, {
        refetchOnMount: false
    });
    return {
        data: query.data,
        isLoading: query.isLoading,
        isError: query.isError
    };
};
export var useFetchSingleBoard = function(boardToken) {
    var retailerToken = getRetailerToken();
    var _useFetchBoardQuery = useFetchBoardQuery(retailerToken, boardToken !== null && boardToken !== void 0 ? boardToken : "", {
        refetchOnMount: false
    }), data = _useFetchBoardQuery.data, isLoading = _useFetchBoardQuery.isPending, refetch = _useFetchBoardQuery.refetch;
    return {
        board: data === null || data === void 0 ? void 0 : data.board,
        isLoading: isLoading,
        refetch: refetch
    };
};
export var useCreateBoard = function() {
    var queryClient = useQueryClient();
    var retailerToken = getRetailerToken();
    var mutation = useCreateBoardMutation({
        onSuccess: function(data) {
            var _data_board;
            var _data_board_token;
            updateAllBoardsCache(queryClient, retailerToken, (_data_board_token = (_data_board = data.board) === null || _data_board === void 0 ? void 0 : _data_board.token) !== null && _data_board_token !== void 0 ? _data_board_token : "", data.board);
        }
    });
    var createBoard = React.useCallback(function(name, isPrivate) {
        return mutation.mutateAsync([
            retailerToken,
            ICreateRetailerBoardRequest.build({
                name: name,
                is_private: isPrivate
            })
        ]);
    }, [
        mutation,
        retailerToken
    ]);
    return {
        createBoard: createBoard
    };
};
export var useDeleteBoard = function() {
    var queryClient = useQueryClient();
    var mutation = useDeleteBoardMutation();
    var retailerToken = getRetailerToken();
    var deleteBoard = React.useCallback(/*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(boardToken) {
            var response;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        return [
                            4,
                            mutation.mutateAsync([
                                retailerToken,
                                boardToken
                            ])
                        ];
                    case 1:
                        response = _state.sent();
                        if (!mutation.isError) {
                            updateAllBoardsCache(queryClient, retailerToken, boardToken, undefined);
                        }
                        return [
                            2,
                            response
                        ];
                }
            });
        });
        return function(boardToken) {
            return _ref.apply(this, arguments);
        };
    }(), [
        queryClient,
        mutation,
        retailerToken
    ]);
    return {
        deleteBoard: deleteBoard
    };
};
export var useUpdateBoard = function() {
    var queryClient = useQueryClient();
    var mutation = useUpdateBoardMutation();
    var retailerToken = getRetailerToken();
    var updateBoard = React.useCallback(/*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(boardToken, updatedBoardData) {
            var response;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        return [
                            4,
                            mutation.mutateAsync([
                                retailerToken,
                                boardToken,
                                IUpdateRetailerBoardRequest.build(updatedBoardData)
                            ])
                        ];
                    case 1:
                        response = _state.sent();
                        if (!mutation.isError) {
                            updateBoardsCaches(queryClient, retailerToken, boardToken, updatedBoardData);
                        }
                        return [
                            2,
                            response
                        ];
                }
            });
        });
        return function(boardToken, updatedBoardData) {
            return _ref.apply(this, arguments);
        };
    }(), [
        queryClient,
        mutation,
        retailerToken
    ]);
    return {
        updateBoard: updateBoard
    };
};
export var updateBoardsCaches = function(queryClient, retailerToken, boardToken, updatedBoardData) {
    updateSingleBoardCache(queryClient, retailerToken, boardToken, updatedBoardData);
    updateAllBoardsCache(queryClient, retailerToken, boardToken, updatedBoardData);
    updateDefaultBoardCache(queryClient, retailerToken, updatedBoardData);
};
var updateAllBoardsCache = function(queryClient, retailerToken, boardToken, updatedBoardData) {
    // this cache is used for a list of all retailer boards data
    var fetchAllBoardsKey = getFetchBoardsQueryKey(retailerToken, PARAMS);
    queryClient.setQueryData(fetchAllBoardsKey, function(cached) {
        if (!cached) {
            return;
        }
        if (!updatedBoardData) {
            return _object_spread_props(_object_spread({}, cached), {
                boards: cached.boards.filter(function(board) {
                    return board.token !== boardToken;
                })
            });
        }
        if (cached.boards.find(function(board) {
            return board.token === boardToken;
        })) {
            return _object_spread_props(_object_spread({}, cached), {
                boards: cached.boards.map(function(board) {
                    return board.token === boardToken ? _object_spread({}, board, updatedBoardData) : board;
                })
            });
        } else {
            return _object_spread_props(_object_spread({}, cached), {
                boards: [
                    IRetailerBoard.build(updatedBoardData)
                ].concat(cached.boards)
            });
        }
    });
};
var updateSingleBoardCache = function(queryClient, retailerToken, boardToken, updatedBoardData) {
    // this cache is used for a single board data
    var fetchBoardKey = getFetchBoardQueryKey(retailerToken, boardToken);
    queryClient.setQueryData(fetchBoardKey, function(cached) {
        if (!cached) {
            return;
        }
        cached.board = _object_spread({}, IRetailerBoard.build(cached.board), updatedBoardData);
        return cached;
    });
};
var updateDefaultBoardCache = function(queryClient, retailerToken, updatedBoardData) {
    // this cache is used for the default board data
    var fetchDefaultBoardKey = getFetchDefaultBoardQueryKey(retailerToken);
    queryClient.setQueryData(fetchDefaultBoardKey, function(cached) {
        if (!cached) {
            return;
        }
        cached.board = _object_spread({}, IRetailerBoard.build(cached.board), updatedBoardData);
        return cached;
    });
};
